/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Grid, Paper } from '@mui/material';

import React, { FC, useEffect } from 'react';
import FilterBar from '../filter-bar';
import GeoAreaTree from '../geo-area-tree';
import useFetchAppVariant from '../hooks/useFetchAppVariant';
import useFetchContracts from '../hooks/useFetchContracts';
import useFetchTenants from '../hooks/useFetchTenants';
import useFilterBar from '../hooks/useFilterBar';
import ListSection from '../list-section';
import useSearch from '../Search/hook/useSearch';
import SelectedContractsSection from '../selected-contracts/SelectedContractsSection';
import SelectedItemDetails from '../selected-item-details';
import { ListType } from '../types';

const ContractAuditDetails: FC = () => {
	const { filters, removeAllFilters, removeFilter, filteredTenantIds, filteredAppVariantIds } = useFilterBar();
	const { searchQueryAppVariant, searchQueryTenant, searchQueryContract } = useSearch();

	const {
		isLoading: isLoadingAppVariants,
		isFetchingNextPage: isFetchingNextPageAppVariants,
		data: appVariants,
		fetchNextPage: fetchNextAppVariantsPage
	} = useFetchAppVariant(filteredTenantIds, searchQueryAppVariant);
	const {
		isLoading: isLoadingTenants,
		data: tenants,
		fetchNextPage: fetchNextTenantsPage,
		isFetchingNextPage: isFetchingNextPageTenants
	} = useFetchTenants(filteredAppVariantIds, searchQueryTenant);
	const {
		isLoading: isLoadingContracts,
		isFetchingNextPage: isFetchingNextPageContracts,
		data: contracts,
		fetchNextPage: fetchNextContractsPage,
		hasNextPage: hasNextContractsPage
	} = useFetchContracts(filteredTenantIds, filteredAppVariantIds, searchQueryContract);

	const isTenantFilterSet = filteredTenantIds.length > 0;

	// Prefetch
	useEffect(() => {
		if (hasNextContractsPage && !isFetchingNextPageContracts) {
			fetchNextContractsPage();
		}
	}, [isFetchingNextPageContracts, hasNextContractsPage, fetchNextContractsPage]);

	return (
		<Paper>
			<Grid
				container
				sx={{
					minHeight: 'calc(100vh - 150px)',
					justifyContent: 'center'
				}}
			>
				<Grid container item xs={12} md={8} spacing={1}>
					<Grid item xs={12}>
						<Paper
							variant="outlined"
							sx={{
								color: 'secondary',
								display: 'flex'
							}}
						>
							<FilterBar filters={filters} handleRemoveFilter={removeFilter} handleRemoveAllFilter={removeAllFilters} />
						</Paper>
					</Grid>
					<Grid xs={12} container item>
						<ListSection
							type={ListType.APP_VARIANT}
							title={'App-Varianten'}
							isLoading={isLoadingAppVariants}
							isFetchingNextPage={isFetchingNextPageAppVariants}
							data={appVariants}
							fetchNextPage={fetchNextAppVariantsPage}
							isTenantFilterSet={isTenantFilterSet}
						/>

						<ListSection
							type={ListType.TENANT}
							title={'Mandanten'}
							data={tenants}
							isLoading={isLoadingTenants}
							isFetchingNextPage={isFetchingNextPageTenants}
							fetchNextPage={fetchNextTenantsPage}
						/>
						<ListSection
							type={ListType.CONTRACT}
							title={'Nutzungsverträge'}
							data={contracts}
							isLoading={isLoadingContracts}
							isFetchingNextPage={isFetchingNextPageContracts}
							fetchNextPage={fetchNextContractsPage}
							showSelectAll={true}
						/>
					</Grid>
					<Grid item xs={12}>
						<SelectedItemDetails />
					</Grid>
				</Grid>
				<Grid container item xs={12} md={4} spacing={0}>
					<Grid item xs={12}>
						<SelectedContractsSection hasNextPage={hasNextContractsPage} />
						<Paper
							variant="outlined"
							sx={{
								color: 'secondary',
								maxHeight: 'calc(100vh - 180px)',
								overflowY: 'scroll'
							}}
						>
							<GeoAreaTree />
						</Paper>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};
export default ContractAuditDetails;
